import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../utils/toast';
import { settingsService } from '../services/settingsService';

export function Settings() {
  const { user } = useAuth();
  
  // Form state
  const [settings, setSettings] = useState(settingsService.getSettings());
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  
  useEffect(() => {
    // Load settings on component mount
    const userSettings = settingsService.getSettings();
    setSettings(userSettings);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newSettings = {
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    };
    setSettings(newSettings);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const success = await settingsService.saveSettings(settings);
      if (success) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('An error occurred while saving settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      const defaultSettings = await settingsService.getDefaultSettings();
      setSettings(defaultSettings);
      toast.success('Settings reset to defaults');
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Account Settings</h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>Manage your account preferences and settings.</p>
          </div>
          
          <form onSubmit={handleSubmit} className="mt-5">
            <div className="space-y-6">
              {/* Profile Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Profile Information</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        className="input-field"
                        value={user?.name || ''}
                        disabled
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        name="email"
                        id="email"
                        className="input-field"
                        value={user?.email || ''}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Appearance Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Appearance</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div>
                    <label htmlFor="theme" className="block text-sm font-medium text-gray-700">
                      Theme
                    </label>
                    <div className="mt-1">
                      <select
                        id="theme"
                        name="theme"
                        className="input-field"
                        value={settings.theme}
                        onChange={handleChange}
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="system">System Default</option>
                        <option value="auto">Auto (Time-based)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700">
                      Font Size
                    </label>
                    <div className="mt-1">
                      <select
                        id="fontSize"
                        name="fontSize"
                        className="input-field"
                        value={settings.fontSize}
                        onChange={handleChange}
                      >
                        <option value="xs">Extra Small</option>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                        <option value="xl">Extra Large</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="colorScheme" className="block text-sm font-medium text-gray-700">
                      Color Scheme
                    </label>
                    <div className="mt-1">
                      <select
                        id="colorScheme"
                        name="colorScheme"
                        className="input-field"
                        value={settings.colorScheme}
                        onChange={handleChange}
                      >
                        <option value="default">Default Blue</option>
                        <option value="green">Forest Green</option>
                        <option value="purple">Royal Purple</option>
                        <option value="red">Ruby Red</option>
                        <option value="orange">Sunset Orange</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="compactView"
                        name="compactView"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.compactView}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="compactView" className="font-medium text-gray-700">
                        Compact View
                      </label>
                      <p className="text-gray-500">Use a more compact layout throughout the application.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="reducedMotion"
                        name="reducedMotion"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.reducedMotion}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="reducedMotion" className="font-medium text-gray-700">
                        Reduced Motion
                      </label>
                      <p className="text-gray-500">Minimize animations and transitions.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="highContrast"
                        name="highContrast"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.highContrast}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="highContrast" className="font-medium text-gray-700">
                        High Contrast
                      </label>
                      <p className="text-gray-500">Increase contrast for better visibility.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preferences Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Preferences</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                      Language
                    </label>
                    <div className="mt-1">
                      <select
                        id="language"
                        name="language"
                        className="input-field"
                        value={settings.language}
                        onChange={handleChange}
                      >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="fr">Français</option>
                        <option value="de">Deutsch</option>
                        <option value="it">Italiano</option>
                        <option value="pt">Português</option>
                        <option value="ru">Русский</option>
                        <option value="zh">中文</option>
                        <option value="ja">日本語</option>
                        <option value="ko">한국어</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
                      Timezone
                    </label>
                    <div className="mt-1">
                      <select
                        id="timezone"
                        name="timezone"
                        className="input-field"
                        value={settings.timezone}
                        onChange={handleChange}
                      >
                        <option value="UTC-12">UTC-12:00</option>
                        <option value="UTC-11">UTC-11:00</option>
                        <option value="UTC-10">UTC-10:00</option>
                        <option value="UTC-9">UTC-09:00</option>
                        <option value="UTC-8">Pacific Time (UTC-08:00)</option>
                        <option value="UTC-7">Mountain Time (UTC-07:00)</option>
                        <option value="UTC-6">Central Time (UTC-06:00)</option>
                        <option value="UTC-5">Eastern Time (UTC-05:00)</option>
                        <option value="UTC-4">Atlantic Time (UTC-04:00)</option>
                        <option value="UTC-3">UTC-03:00</option>
                        <option value="UTC-2">UTC-02:00</option>
                        <option value="UTC-1">UTC-01:00</option>
                        <option value="UTC+0">UTC±00:00</option>
                        <option value="UTC+1">Central European Time (UTC+01:00)</option>
                        <option value="UTC+2">Eastern European Time (UTC+02:00)</option>
                        <option value="UTC+3">UTC+03:00</option>
                        <option value="UTC+4">UTC+04:00</option>
                        <option value="UTC+5">UTC+05:00</option>
                        <option value="UTC+6">UTC+06:00</option>
                        <option value="UTC+7">UTC+07:00</option>
                        <option value="UTC+8">China Standard Time (UTC+08:00)</option>
                        <option value="UTC+9">Japan Standard Time (UTC+09:00)</option>
                        <option value="UTC+10">UTC+10:00</option>
                        <option value="UTC+11">UTC+11:00</option>
                        <option value="UTC+12">UTC+12:00</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700">
                      Date Format
                    </label>
                    <div className="mt-1">
                      <select
                        id="dateFormat"
                        name="dateFormat"
                        className="input-field"
                        value={settings.dateFormat}
                        onChange={handleChange}
                      >
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        <option value="MMM DD, YYYY">MMM DD, YYYY</option>
                        <option value="DD MMM YYYY">DD MMM YYYY</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeFormat" className="block text-sm font-medium text-gray-700">
                      Time Format
                    </label>
                    <div className="mt-1">
                      <select
                        id="timeFormat"
                        name="timeFormat"
                        className="input-field"
                        value={settings.timeFormat}
                        onChange={handleChange}
                      >
                        <option value="12">12-hour (AM/PM)</option>
                        <option value="24">24-hour</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notifications Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Notifications</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="emailNotifications"
                        name="emailNotifications"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.emailNotifications}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="emailNotifications" className="font-medium text-gray-700">
                        Email notifications
                      </label>
                      <p className="text-gray-500">Receive email notifications for important updates.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="desktopNotifications"
                        name="desktopNotifications"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.desktopNotifications}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="desktopNotifications" className="font-medium text-gray-700">
                        Desktop notifications
                      </label>
                      <p className="text-gray-500">Receive desktop notifications for important updates.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="weeklyDigest"
                        name="weeklyDigest"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.weeklyDigest}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="weeklyDigest" className="font-medium text-gray-700">
                        Weekly digest
                      </label>
                      <p className="text-gray-500">Receive a weekly summary of your activity.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="productUpdates"
                        name="productUpdates"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.productUpdates}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="productUpdates" className="font-medium text-gray-700">
                        Product Updates
                      </label>
                      <p className="text-gray-500">Receive notifications about new features and improvements.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="securityAlerts"
                        name="securityAlerts"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.securityAlerts}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="securityAlerts" className="font-medium text-gray-700">
                        Security Alerts
                      </label>
                      <p className="text-gray-500">Receive notifications about security-related events.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Privacy & Security</h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="twoFactorAuth"
                        name="twoFactorAuth"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.twoFactorAuth}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="twoFactorAuth" className="font-medium text-gray-700">
                        Two-Factor Authentication
                      </label>
                      <p className="text-gray-500">Enable additional security for your account.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="activityLogging"
                        name="activityLogging"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.activityLogging}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="activityLogging" className="font-medium text-gray-700">
                        Activity Logging
                      </label>
                      <p className="text-gray-500">Log all account activity for security purposes.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="dataCollection"
                        name="dataCollection"
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        checked={settings.dataCollection}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="dataCollection" className="font-medium text-gray-700">
                        Usage Data Collection
                      </label>
                      <p className="text-gray-500">Allow collection of usage data to improve our services.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end space-x-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleReset}
                disabled={isResetting}
              >
                {isResetting ? 'Resetting...' : 'Reset to Defaults'}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 